import { Banner } from "components/DesignSystem/Banner/Banner";
import { ResyncInvoiceModal } from "components/InvoiceAction/ResyncInvoiceModal";
import { LockedUi } from "components/LockedUi/LockedUi";
import { INVOICE_SOURCE_SYNC_STATUS } from "constants/invoiceSourceSync";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { REVENUE_DATA_SOURCE_OPTIONS } from "constants/revenueDataSource";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { ReactNode } from "react";
import { Invoice } from "types/Models/invoice";
import { InvoiceSourceSync } from "types/Models/invoiceSourceSync";
import { formatDate } from "utils/formatDate";
import { formatSourceSyncValue } from "utils/formatSourceSyncValue";
import { sortSourceChanges } from "utils/sortSourceChanges";
import { getSourceRemoval } from "utils/getSourceRemoval";
import { isSourceSyncInLockPeriod } from "utils/isSourceSyncInLockPeriod";
import { LockedResyncTooltip } from "./LockedResyncTooltip";
import { ResyncInvoiceButton } from "./ResyncInvoiceButton";

type InvoiceSourceSyncBannerProps = {
  invoice: Invoice;
};

type ResyncActionProps = {
  invoice: Invoice;
  sourceSync: InvoiceSourceSync;
};

const ResyncAction = ({ invoice, sourceSync }: ResyncActionProps) => {
  const entityId = useCurrentEntityId();
  const resyncModal = useModal();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const isLocked = isSourceSyncInLockPeriod({
    invoiceDate: invoice.invoice_date,
    sourceSync,
    lockDate: bookkeepingSettings?.accounting_lock_date,
  });

  const blocker: ReactNode = isLocked ? <LockedResyncTooltip /> : null;

  return (
    <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
      <ResyncInvoiceButton blocker={blocker} onClick={resyncModal.open} />
      {resyncModal.isOpen && (
        <ResyncInvoiceModal
          invoice={invoice}
          open={resyncModal.isOpen}
          onClose={resyncModal.close}
        />
      )}
    </LockedUi>
  );
};

type ChangeSummaryProps = {
  sourceSync: InvoiceSourceSync;
  sourceName: string;
  currencyCode?: string | null;
};

// Every changed field with its value on each side, most significant first.
// Spans with list roles because the Banner renders the description inside a <p>.
const ChangeSummary = ({
  sourceSync,
  sourceName,
  currencyCode,
}: ChangeSummaryProps) => (
  <span className="t-flex t-flex-col t-gap-0.5">
    <span>Changes made:</span>
    <span role="list" className="t-flex t-flex-col t-gap-0.5 t-pl-1.5">
      {sortSourceChanges(sourceSync).map((change) => (
        <span role="listitem" key={change.field} className="t-flex t-gap-2">
          <span aria-hidden="true">•</span>
          <span>
            {change.label}:{" "}
            {formatSourceSyncValue(change.field, change.source, currencyCode)}{" "}
            (On {sourceName}) <span className="t-px-1">|</span>{" "}
            {formatSourceSyncValue(change.field, change.inkle, currencyCode)}{" "}
            (On Inkle)
          </span>
        </span>
      ))}
    </span>
  </span>
);

export const InvoiceSourceSyncBanner = ({
  invoice,
}: InvoiceSourceSyncBannerProps) => {
  const sourceSync = invoice.source_sync;
  if (!sourceSync) {
    return null;
  }

  const sourceName = REVENUE_DATA_SOURCE_OPTIONS[invoice.source];
  const removal = getSourceRemoval(sourceSync);

  if (sourceSync.status === INVOICE_SOURCE_SYNC_STATUS.VOIDED_SOURCE_DELETED) {
    const reason =
      removal === "Cancelled"
        ? `was cancelled in ${sourceName}`
        : `no longer exists in ${sourceName}`;
    return (
      <Banner
        type="neutral"
        title={`${removal ?? "Deleted"} in ${sourceName} and voided`}
        description={`This invoice ${reason}, so it was voided on ${formatDate(
          invoice.marked_void_at
        )} and a void journal entry was posted.`}
      />
    );
  }

  const isOutOfSync =
    sourceSync.status === INVOICE_SOURCE_SYNC_STATUS.CHANGED ||
    sourceSync.status === INVOICE_SOURCE_SYNC_STATUS.NEEDS_REVIEW;
  if (!isOutOfSync) {
    return null;
  }

  // Deleted or cancelled at source but the void was blocked: resync can't help, so no action
  if (removal) {
    return (
      <Banner
        type="error"
        title={`${removal} in ${sourceName}, but it couldn't be voided`}
        titleClassName="t-text-text-100"
        description={sourceSync.blocker ?? ""}
      />
    );
  }

  return (
    <Banner
      type="warning"
      title={`This invoice is out of sync with ${sourceName}`}
      titleClassName="t-text-text-100"
      description={
        <ChangeSummary
          sourceSync={sourceSync}
          sourceName={sourceName}
          currencyCode={invoice.currency}
        />
      }
      cta={<ResyncAction invoice={invoice} sourceSync={sourceSync} />}
    />
  );
};
